import type {Query, QueryKey} from '@tanstack/react-query'

import type {Group, GroupedMemexItems, MemexItem} from '../../../api/memex-items/contracts'
import type {PageParam, PaginatedMemexItemsQueryVariables, SliceByQueryVariables} from './types'

export const paginatedMemexItemsQueryKey = 'paginatedMemexItems'
export const pageParamsQueryKey = 'pageParamsData'
export const sliceByDataQueryKey = 'sliceByData'
export const totalCountsQueryKey = 'paginatedTotalCounts'

export const pageTypeForUngroupedItems = 'ungrouped'
export const pageTypeForGroups = 'groups'
export type PageTypeForUngroupedItems = typeof pageTypeForUngroupedItems
export type PageTypeForGroups = typeof pageTypeForGroups
export type GroupId = string
export type PageTypeForGroupedItems = {groupId: GroupId}
export type PageType = PageTypeForGroupedItems | PageTypeForUngroupedItems | PageTypeForGroups

export type GroupedMemexItemsQueryKey = [
  typeof paginatedMemexItemsQueryKey,
  PaginatedMemexItemsQueryVariables,
  PageTypeForGroupedItems,
  PageParam,
]

export type UngroupedMemexItemsQueryKey = [
  typeof paginatedMemexItemsQueryKey,
  PaginatedMemexItemsQueryVariables,
  PageTypeForUngroupedItems,
  PageParam,
]

export type MemexGroupsQueryKey = [
  typeof paginatedMemexItemsQueryKey,
  PaginatedMemexItemsQueryVariables,
  PageTypeForGroups,
  PageParam,
]

export type PaginatedMemexItemsQueryKey = GroupedMemexItemsQueryKey | UngroupedMemexItemsQueryKey | MemexGroupsQueryKey

export const buildMemexItemsOrGroupsQueryKey = (
  variables: PaginatedMemexItemsQueryVariables,
  pageType: PageType,
  pageParam: PageParam,
): PaginatedMemexItemsQueryKey => {
  return [paginatedMemexItemsQueryKey, variables, pageType, pageParam]
}

export function buildUngroupedMemexItemsQueryKey(
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
): UngroupedMemexItemsQueryKey {
  return [paginatedMemexItemsQueryKey, variables, pageTypeForUngroupedItems, pageParam]
}

export function buildMemexGroupsQueryKey(
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
): MemexGroupsQueryKey {
  return [paginatedMemexItemsQueryKey, variables, pageTypeForGroups, pageParam]
}

export function buildGroupedMemexItemsQueryKey(
  variables: PaginatedMemexItemsQueryVariables,
  pageType: PageTypeForGroupedItems,
  pageParam: PageParam,
): GroupedMemexItemsQueryKey {
  return [paginatedMemexItemsQueryKey, variables, pageType, pageParam]
}

export type PageParamsQueryKey = [
  typeof paginatedMemexItemsQueryKey,
  PaginatedMemexItemsQueryVariables,
  typeof pageParamsQueryKey,
]

export type SliceByDataQueryKey = [
  typeof paginatedMemexItemsQueryKey,
  PaginatedMemexItemsQueryVariables,
  typeof sliceByDataQueryKey,
]

export type TotalCountsDataQueryKey = [
  typeof paginatedMemexItemsQueryKey,
  PaginatedMemexItemsQueryVariables,
  typeof totalCountsQueryKey,
]

export const buildPageParamsQueryKey = (variables: PaginatedMemexItemsQueryVariables): PageParamsQueryKey => {
  return [paginatedMemexItemsQueryKey, variables, pageParamsQueryKey]
}

export const buildSliceDataQueryKey = (variables: SliceByQueryVariables): SliceByDataQueryKey => {
  return [paginatedMemexItemsQueryKey, variables, sliceByDataQueryKey]
}

export const buildPaginatedTotalCountsQueryKey = (
  variables: PaginatedMemexItemsQueryVariables,
): TotalCountsDataQueryKey => {
  return [paginatedMemexItemsQueryKey, variables, totalCountsQueryKey]
}

/**
 * A utility method for looking up the pageType from a `react-query` `QueryKey`. The `QueryKey`
 * is implemented as an Array under the hood.
 * @param queryKey A query key for paginated memex items data.
 * @returns The pageType from the query key.
 */
export const getPageTypeFromQueryKey = (queryKey: PaginatedMemexItemsQueryKey): PageType => {
  return queryKey[2]
}

export const isQueryKeyForGroups = (queryKey: PaginatedMemexItemsQueryKey): boolean => {
  // A query key that looks like:
  // ['paginatedMemexItems', {q: '', sortedBy: [], horizontalGroupedByColumnId: 'Status'}, undefined, undefined]
  // Represents a page of groups
  return (
    queryKey[2] === pageTypeForGroups &&
    (queryKey[1].horizontalGroupedByColumnId != null || queryKey[1].verticalGroupedByColumnId != null)
  )
}

export const isQueryKeyForGroupedItems = (
  queryKey: PaginatedMemexItemsQueryKey,
): queryKey is GroupedMemexItemsQueryKey => {
  return isPageTypeForGroupedItems(queryKey[2])
}

export const isPageTypeForGroupedItems = (pageType: PageType): pageType is PageTypeForGroupedItems => {
  return typeof pageType !== 'string'
}

export const createGroupedItemsPageType = (groupId: string): PageTypeForGroupedItems => {
  return {groupId}
}

export const createGroupedItemsPageTypeFromGroup = (
  group: GroupedMemexItems<MemexItem> | Group,
): PageTypeForGroupedItems => {
  return {groupId: group.groupId}
}

export const createGroupIdKeyFromPageType = (pageType: PageTypeForGroupedItems) => {
  return pageType.groupId
}

/**
 * Given a single query, checks to see whether or not the query represents data
 * indirectly related to memex items or groups, e.g. Page Params, Total Counts, Slice By Data, etc.
 *
 * This helper is used as the `predicate` when filtering queries with `get`/`setQueriesData`.
 *
 * All queries in the query cache related to memex items and groups live under a top-level
 * 'paginatedMemexItems` query key, so we use that top-level query key when we want to inspect or
 * modify all of these queries broadly, like during cache invalidation.
 *
 * However, we often _do not_ want to consider the sort of ancillary data referenced in this function,
 * so by using this function as a predicate, we can filter these queries out early by inspecting
 * the last part of the query key.
 */
export const isQueryForItemsMetadata = (query: Query<unknown, Error, unknown, QueryKey>) => {
  const queryKey = query.queryKey
  const lastPartOfQueryKey = queryKey[queryKey.length - 1]
  return (
    lastPartOfQueryKey === pageParamsQueryKey ||
    lastPartOfQueryKey === totalCountsQueryKey ||
    lastPartOfQueryKey === sliceByDataQueryKey
  )
}
